/**
 * The game store: a thin reactive shell around the pure engine.
 *
 * It owns the authoritative GameState, dispatches Actions through `reduce`,
 * reconciles the result into a Solid store for fine-grained updates, keeps a
 * history stack for undo, and surfaces the latest events + any rejection reason.
 * All game mutation flows through `dispatch` — components never edit state.
 */
import { batch } from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import { reduce, type Action, type GameEvent, type GameState } from "../core";

/**
 * The minimal surface the interaction controller needs from whatever is driving
 * the game. Both the local engine-backed store and the online (WebSocket)
 * session implement it, so the same controller + UI work in either mode.
 */
export interface GameBackend {
  readonly state: GameState;
  readonly error: string | null;
  readonly canUndo: boolean;
  dispatch(action: Action): boolean;
  undo(): void;
  clearError(): void;
}

export interface GameStore extends GameBackend {
  readonly lastEvents: readonly GameEvent[];
}

interface InternalState {
  state: GameState;
  lastEvents: GameEvent[];
  error: string | null;
}

export function createGameStore(initial: GameState): GameStore {
  const [store, setStore] = createStore<InternalState>({
    state: initial,
    lastEvents: [],
    error: null,
  });

  // History of prior states for undo. Kept outside the reactive store.
  const history: GameState[] = [];

  return {
    get state() {
      return store.state;
    },
    get lastEvents() {
      return store.lastEvents;
    },
    get error() {
      return store.error;
    },
    get canUndo() {
      return history.length > 0;
    },
    dispatch(action: Action): boolean {
      const result = reduce(store.state, action);
      if (!result.ok) {
        setStore("error", result.error);
        return false;
      }
      const previous = store.state;
      batch(() => {
        history.push(previous);
        setStore("state", reconcile(result.state));
        setStore("lastEvents", result.events as GameEvent[]);
        setStore("error", null);
      });
      return true;
    },
    undo() {
      const previous = history.pop();
      if (!previous) return;
      batch(() => {
        setStore("state", reconcile(previous));
        setStore("lastEvents", []);
        setStore("error", null);
      });
    },
    clearError() {
      setStore("error", null);
    },
  };
}
